import { Button, CircularProgress, MenuItem, Select, Typography } from "@mui/material";
import images from "../../../assets/images";
import styles from "./EmailUnsubscribe.module.css";
import { KeyboardArrowDown } from "@mui/icons-material";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { GET_UNSUBSCRIBE_REASONS, GET_USER_EMAIL_PREFERENCES, UPDATE_USER_EMAIL_PREFERENCE } from "./api/EmailUnsubscribeQuery";
import ToasterWithMessage from "../toaster";
import { useEffect, useState } from "react";
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  userId?: string; 
}

const EmailUnsubscribe = () => {

  const { QuickAsystMailLogo } = images;
  const { error, success} = ToasterWithMessage();
  const [listOfReasons, setListOfReasons] = useState([]);
  const [token, setToken] = useState("");
  const [type, setType] = useState("");
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [isAlreadyUnsubscribed, setIsAlreadyUnsubscribed] = useState(false);
  const [isLoadingPreference, setIsLoadingPreference] = useState(true);

  const [updateEmailPreference, { loading }] = useMutation(UPDATE_USER_EMAIL_PREFERENCE, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      success(data.updateUserEmailPreference.message);
      setStatus("success");
      setMessage(data.updateUserEmailPreference.message);
    },
    onError: (err: ApolloError) => {
      error(err.message);
      setStatus("error");
      setMessage(err.message);
    },
  });

  const handleSubscribe = async () => {
    if (!selectedReason) {
      error("Please select a reason.");
      return;
    }

    const input: any = {
      reasonId: selectedReason,
      token,
    };

    if (type === "season") {
      input.unSubscribeSeasonEmail = true;
    } else if (type === "weekly") {
      input.unSubscribeWeeklyEmail = true;
    }

    await updateEmailPreference({
      variables: {
        input
      }
    });
  };

  const [getListOfUnsubscribeReasons] = useLazyQuery(GET_UNSUBSCRIBE_REASONS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setListOfReasons(data.unsubscribe_reasons);
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  const [getUserEmailPreference] = useLazyQuery(GET_USER_EMAIL_PREFERENCES, {
  fetchPolicy: "no-cache",
  onCompleted: (data) => {
    const user = data.user?.[0];
    if (user) {
      const unsubscribed =
        (type === "weekly" && user.u_weekly_email_enabled === false) ||
        (type === "season" && user.u_season_email_enabled === false);

      setIsAlreadyUnsubscribed(unsubscribed);
    }
    setIsLoadingPreference(false);
  },
  onError: (err) => {
    error(err?.message);
    setIsLoadingPreference(false);
  }
});

  useEffect(() => {
    getListOfUnsubscribeReasons();
  }, []);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token') ?? '';
  const urlType = params.get('type') ?? '';

  setToken(urlToken);
  setType(urlType);
  
  if (urlToken && urlType) {
    try {
      const decoded = jwtDecode<JwtPayload>(urlToken);
      
      if(decoded.userId){
        console.log('user id not found on token');
      }
      getUserEmailPreference({ variables: { userId: decoded.userId } });
    } catch (e) {
      console.error('Token decode failed:', e);
      error('Something went wrong!');
      setErrorMessage(true);
      setIsLoadingPreference(false);
    }
  } else {
    setIsLoadingPreference(false);
  }
}, []);

  const getHeading = () => {
    if (type === "season")
      return "Want to unsubscribe from season start reminder emails?";
    if (type === "weekly")
      return "Want to unsubscribe from weekly start reminder emails?";
    return "Want to unsubscribe from emails?";
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerImage}>
        <img
          style={{ width: '250px' }}
          alt="sidebarLogo"
          src={QuickAsystMailLogo}
          data-testid="sidebarLogo"
        />
      </div>

      <div className={styles.contentContainer}>
        {
          isLoadingPreference ? (
          <div> <CircularProgress color="primary" size={35} /></div>) :
          errorMessage ?
            <div className={styles.pageNotFound}>
              Page not found!
            </div>
            : (status !== 'idle') ?
              <div className={status === 'error' ? styles.pageNotFound : styles.successMessage}>
                {message}
              </div> : isAlreadyUnsubscribed ? (
                <div className={styles.successMessage}>
                  {type === "weekly"
                    ? "Weekly emails are already unsubscribed."
                    : "Seasonal emails are already unsubscribed."}
                </div>
              ) :
              <div className={styles.pageWrapper}>
                <div className={styles.headerText}>{getHeading()}</div>
                <div className={styles.header1}>
                  {`QuickAsyst will no longer send you emails once you confirm below.`}
                </div>
                <div className={styles.formContainer}>
                  <div className={styles.selectLabel}>Reason for Unsubscription</div>
                  <Select
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    displayEmpty
                    placeholder="Select Reason"
                    IconComponent={KeyboardArrowDown}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      '& .MuiSelect-select': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                      },
                      '& .MuiOutlinedInput-root': {
                        width: '100%',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ccc',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#aaa',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--branding-secondary)',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: 'transparent',
                          },
                          '& .MuiMenuItem-root.Mui-selected:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#888' }}>Select Reason</span>;
                      }
                      const reason: any = listOfReasons.find((r: any) => r.ur_id === selected);
                      return (
                        <Typography sx={{
                          color: 'var(--neutral-1000)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                        }}
                        title={reason?.ur_reason_text}
                        >
                          {reason?.ur_reason_text}
                        </Typography>
                      );
                    }}
                    variant="outlined"
                  >
                    {listOfReasons.map((item: any) => (
                      <MenuItem key={item.ur_id} value={item.ur_id}>
                        {item.ur_reason_text}
                      </MenuItem>
                    ))}
                  </Select>
                  <div className={styles.buttonContainer}>
                    <div className={(!selectedReason ? styles.buttonStyleDisabled : styles.buttonStyle)}>
                      <Button
                        classes={{
                          root: styles.submitBtn
                        }}
                        disabled={loading || !selectedReason}
                        onClick={handleSubscribe}
                        fullWidth
                      >
                        Submit
                      </Button>
                    </div>
                  </div>

                </div>
              </div>
        }

      </div>
    </div>
  );
}

export default EmailUnsubscribe;